<template>
  <SfModalPortal
    class="sf-border-box"
    :modal-id="'account-entries-modal'"
    :content-padding="0"
    :size="modalSizes.l"
    scrollable-content
    data-walkthrough-id="account-entries-modal"
    @opened="openModal"
    @close="close"
  >
    <template #title>
      <div class="sf-level sf-level-center">
        <div class="sf-c-text-light">
          {{ I18n.t('account_entries_modal.total_value') }} {{ unitLabel }}:
        </div>
        <strong>{{ totalValue }}</strong>
        <div class="sf-label">{{ periodName }}</div>
      </div>
    </template>

    <template #body>
      <div v-if="isLoading" class="sf-level sf-level-center tw-p-6">
        <div data-testid="spinner" class="sf-spinner"></div>
      </div>
      <div v-else>
        <div class="tw-p-6 tw-sticky tw-top-0 tw-bg-white tw-z-10">
          <SfSearch
            v-model="filterText"
            class="tw-mb-4"
            :placeholder="I18n.t('account_entries_modal.search_accounts')"
            show-clear-icon
            :show-result-navigation="false"
            @clear="clearFilterText"
          />
        </div>
        <EmptyState
          v-if="isFilteredEmpty"
          class="tw-p-6"
          :text="I18n.t('account_entries_modal.search_empty')"
        />
        <div v-else class="tw-relative tw-z-0 tw-pl-6 tw-pr-6 tw-pb-6">
          <SfTable
            :headers="headers"
            :rows="accountEntriesToShow"
            :variant="tableVariants.FLAT"
            :hide-header="!unitLabel"
            rounded
          >
            <template #header="{ headerIndex, headerLabel }">
              <SfTableHeaderCell
                :class="
                  headerIndex === 1
                    ? 'tw-w-full tw-uppercase tw-justify-end'
                    : ''
                "
                :header-index="headerIndex"
                :header-id="headerLabel"
                :sortable="false"
              >
                {{ headerLabel }}
              </SfTableHeaderCell>
            </template>
            <template #row="{ rowData }">
              <SfTableRowCollapsibleGroup
                v-if="isAccountEntryRowGroup(rowData)"
                :ignored-columns="ignoredColumns"
                :is-expanded="!!expandedGroups[rowData.group]"
                :row-data="rowData"
                :variant="tableVariants.FLAT"
                @toggle-visibility="handleVisibilityChange"
              >
                <template #cell-display="{ index }">
                  <AccountNameCell
                    v-if="index === 0"
                    :account-name="rowData.accountNumberWithName"
                    :original-account-name="
                      rowData.accountOriginalNumberWithOriginalName
                    "
                    :new-adjustment-path="rowData.newAdjustmentPath"
                    @clicked-new-adjustment="close"
                  />
                  <AmountCell
                    v-else
                    :amount="rowData.value"
                    bold
                    :url="rowData.url"
                  />
                </template>
              </SfTableRowCollapsibleGroup>
              <AdjustmentsValueRow
                v-else-if="
                  isAccountEntryRowAdjustments(rowData) &&
                  expandedGroups[rowData.groupBy]
                "
                :adjustments-value="rowData.adjustmentsValue"
                :new-adjustment-path="rowData.newAdjustmentPath"
                :group="String(rowData.groupBy)"
                :url="rowData.url"
                @clicked-new-adjustment="close"
              />
              <SfTableRowCollapsibleItem
                v-else-if="
                  isAccountEntryRowChild(rowData) &&
                  expandedGroups[rowData.groupBy]
                "
                :ignored-columns="ignoredColumns"
                :row-data="rowData"
                :variant="tableVariants.FLAT"
              >
                <template #cell-display="{ index }">
                  <SubsidiaryNameCell
                    v-if="index === 0"
                    :account-name="
                      rowData.accountOriginalNumberWithOriginalName ||
                      rowData.accountNumberWithName
                    "
                    :subsidiary-name="rowData.subsidiaryName"
                  />
                  <AmountCell
                    v-else
                    :amount="rowData.value"
                    :url="rowData.url"
                  />
                </template>
              </SfTableRowCollapsibleItem>
              <SfTableRow
                v-else-if="isAccountEntryRowPlain(rowData)"
                :ignored-columns="ignoredColumns"
                :row-data="rowData"
                :variant="tableVariants.FLAT"
              >
                <template #cell-display="{ index }">
                  <AccountNameCell
                    v-if="index === 0"
                    :account-name="`${rowData.accountNumberWithName}`"
                    :original-account-name="
                      rowData.accountOriginalNumberWithOriginalName
                    "
                    :new-adjustment-path="rowData.newAdjustmentPath"
                    @clicked-new-adjustment="close"
                  />
                  <AmountCell
                    v-else
                    :amount="rowData.value"
                    :redacted="!rowData.url"
                    :url="rowData.url"
                  />
                </template>
              </SfTableRow>
            </template>
          </SfTable>
        </div>
      </div>
    </template>

    <template #footer>
      <div
        v-if="!isLoading && containsSubsidiaryEntries"
        class="
          tw-text-sf-neutral-dark-1
          tw-leading-[1]
          tw-w-full
          tw-flex
          tw-flex-col
          tw-items-start
          tw-pt-6
        "
      >
        <div>{{ I18n.t('account_entries_modal.account_assign_prompt') }}</div>
        <div>
          <a
            class="
              sf-link
              tw-inline-flex tw-items-center tw-leading-[20px]
              !tw-no-underline
              tw-gap-[2px]
            "
            :href="generateAccountLink()"
            @click="handleAccountLinksVisit"
          >
            <span>{{
              I18n.t('account_entries_modal.account_assign_link')
            }}</span>
            <i
              class="fa-solid fa-up-right-from-square tw-text-xs tw-w-3 tw-h-3"
            ></i>
          </a>
        </div>
      </div>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import {
  isAccountEntryRowGroup,
  isAccountEntryRowChild,
  isAccountEntryRowPlain,
  isAccountEntryRowAdjustments,
} from './predicates'
import type { AccountEntry, AccountEntryRow } from './types'
import type { ModalPortalPayloadMap } from '@app/types'

import { ref, computed, watch } from 'vue'

import { getAccountEntries as getAccountEntriesApi } from './api/get-account-entries'
import AccountNameCell from './account-name-cell.vue'
import AmountCell from './amount-cell.vue'
import SubsidiaryNameCell from './subsidiary-name-cell.vue'
import AdjustmentsValueRow from './adjustments-value-row.vue'
import { generateAccountLink } from './utils/generate-account-link'
import { SfLog } from '@app/adapters/logger'
import { tableVariants } from '@app/shared/vue/components/sf-table/constants'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import { modalSizes } from '@app/shared/vue/components/sf-modal/constants'
import SfTable from '@app/shared/vue/components/sf-table/index.vue'
import SfSearch from '@app/shared/vue/components/sf-search/search-input.vue'
import SfTableHeaderCell from '@app/shared/vue/components/sf-table/sf-table-header-cell.vue'
import SfTableRow from '@app/shared/vue/components/sf-table/sf-table-row.vue'
import SfTableRowCollapsibleGroup from '@app/shared/vue/components/sf-table/sf-table-row-collapsible-group.vue'
import SfTableRowCollapsibleItem from '@app/shared/vue/components/sf-table/sf-table-row-collapsible-item.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import EmptyState from '@app/shared/vue/components/molecules/empty-state.vue'
import { doesAccountNameSatisfyQuery } from '@app/utils/account-search-helpers'

const I18n = useI18n()

defineOptions({
  name: 'AccountEntriesModal',
})

const ignoredColumns = [
  'accountId',
  'id',
  'group',
  'groupBy',
  'accountOriginalNumberWithOriginalName',
  'url',
  'newAdjustmentPath',
  'subsidiaryName',
  'subsidiaryAccountEntries',
  'adjustmentsValue',
]

const isLoading = ref(false)
const periodName = ref<string | undefined>('')
const filterText = ref('')
const totalValue = ref<string | undefined>('')
const accountEntries = ref<AccountEntry[]>([])
const expandedGroups = ref<Record<string, boolean>>({})
const unit = ref<string | undefined>('')

const accountEntriesToShow = computed(() => {
  return accountEntries.value.reduce<Array<AccountEntryRow>>(
    (entries, entry) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { subsidiaryAccountEntries, accountId, ...mainEntry } = entry

      const hasSubsidiaryEntries = entry?.subsidiaryAccountEntries?.length > 0
      const filtersMatchMainEntry =
        filterText.value === '' || accountEntryIncludesText(entry)

      if (hasSubsidiaryEntries) {
        const filtersMatchSubsidiaryEntries =
          entry.subsidiaryAccountEntries?.some((subEntry) =>
            accountEntryIncludesText(subEntry)
          )

        const filtersMatchEither =
          filtersMatchMainEntry || filtersMatchSubsidiaryEntries

        if (filtersMatchEither) {
          entries.push({
            ...mainEntry,
            id: accountId,
            group: `${accountId}`,
          })
        }

        if (
          filtersMatchEither &&
          expandedGroups.value[entry.accountId] &&
          entry.adjustmentsValue !== undefined &&
          entry.adjustmentsValue !== null
        ) {
          entries.push({
            id: `adjustments-${accountId}`,
            groupBy: entry.accountId,
            adjustmentsValue: entry.adjustmentsValue,
            newAdjustmentPath: entry.newAdjustmentPath,
            url: entry.url,
          })
        }

        entries.push(
          ...entry.subsidiaryAccountEntries
            .filter(
              (subEntry) =>
                expandedGroups.value[entry.accountId] &&
                (filterText.value === '' ||
                  filtersMatchMainEntry ||
                  accountEntryIncludesText(subEntry))
            )
            .map((subEntry) => ({
              ...subEntry,
              id: subEntry.accountId,
              groupBy: entry.accountId,
            }))
        )
      } else {
        if (filtersMatchMainEntry) {
          entries.push({
            ...mainEntry,
            id: accountId,
          })
        }
      }

      return entries
    },
    []
  )
})

const isFilteredEmpty = computed(() => {
  return !!filterText.value && accountEntriesToShow.value.length === 0
})

const containsSubsidiaryEntries = computed(() => {
  return accountEntries.value.some(
    (entry) => entry.subsidiaryAccountEntries?.length > 0
  )
})

const unitLabel = computed(() => {
  return unit.value ? `(${unit.value})` : ''
})

const openModal = ({
  totalValue: totalValueParam,
  periodName: periodNameParam,
  ledgerId,
  companyId,
  ranges,
  includeZerosValue,
  addRoundingDifference,
  unit: unitParam,
  precision,
  analyticalDimensionId,
  starred,
}: ModalPortalPayloadMap['account-entries-modal']) => {
  isLoading.value = true
  periodName.value = periodNameParam
  totalValue.value = totalValueParam
  filterText.value = ''
  unit.value = unitParam

  getAccountEntries({
    ledgerId,
    companyId,
    ranges,
    includeZerosValue,
    addRoundingDifference,
    unit: unitParam,
    precision,
    analyticalDimensionId,
    starred,
  })
}

const headers = computed(() => {
  return ['', unitLabel.value]
})

const close = () => {
  window.modalCitizen.closeModalPortal()
}

const getAccountEntries = async ({
  companyId,
  ledgerId,
  ranges,
  includeZerosValue,
  addRoundingDifference,
  unit: unitParam,
  precision,
  analyticalDimensionId,
  starred,
}: Omit<
  ModalPortalPayloadMap['account-entries-modal'],
  'totalValue' | 'periodName'
>) => {
  try {
    const response = await getAccountEntriesApi({
      companyId,
      ranges,
      ledgerId,
      includeZerosValue,
      addRoundingDifference,
      unit: unitParam,
      precision,
      analyticalDimensionId,
      starred,
    })

    if (response?.accountEntries) {
      accountEntries.value = response.accountEntries
    }
  } catch (error) {
    SfLog.warn('Failed to fetch account entries', { extra: { error } })
  } finally {
    isLoading.value = false
  }
}

const accountEntryIncludesText = <
  AE extends {
    accountNumberWithName: string
    accountOriginalNumberWithOriginalName: string
  }
>(
  accountEntry: AE
) => {
  const inName = doesAccountNameSatisfyQuery(
    accountEntry.accountNumberWithName,
    filterText.value
  )
  const inOriginalName = doesAccountNameSatisfyQuery(
    accountEntry.accountOriginalNumberWithOriginalName,
    filterText.value
  )
  return inName || inOriginalName
}

function handleVisibilityChange(payload: {
  group: string
  isExpanded: boolean
}) {
  const groupId = parseInt(payload.group, 10)
  expandedGroups.value = {
    ...expandedGroups.value,
    [groupId]: payload.isExpanded,
  }
}

const clearFilterText = () => {
  filterText.value = ''
}

watch(filterText, () => {
  // When searching, reset expanded groups and only expand matching entries
  if (filterText.value !== '') {
    const entriesToExpand = accountEntries.value.filter((entry) => {
      const hasSubsidiaryEntries = entry?.subsidiaryAccountEntries?.length > 0

      if (hasSubsidiaryEntries) {
        const filtersMatchMainEntry = accountEntryIncludesText(entry)

        const filtersMatchSubsidiaryEntries =
          entry.subsidiaryAccountEntries?.some((subEntry) =>
            accountEntryIncludesText(subEntry)
          )

        return filtersMatchMainEntry || filtersMatchSubsidiaryEntries
      }

      return false
    })

    // Reset expanded groups and only expand matching entries
    entriesToExpand.forEach((entry) => {
      expandedGroups.value[entry.accountId] = true
    })
  }
  // When search is cleared, collapse all groups
  else {
    expandedGroups.value = {}
  }
})

const handleAccountLinksVisit = (e: Event) => {
  e.preventDefault()
  e.stopPropagation()

  close()

  window.Turbolinks.visit(generateAccountLink())
}

defineExpose({
  openModal,
})
</script>
